import { ReactNode } from 'react';
import { Link } from 'react-router';
import { fetchGamesGroupedByGameGroup } from '@/lib/api';
import { getFirstGameByRTP } from '@/lib/game';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';
import GameCard from '@/components/GameCard/GameCard';
import ExportToExcelButton from '@/components/ExportToExcelButton/ExportToExcelButton';
import { ga } from '@/lib/analytics';

export type ViewAllTo = {
  game_status: string[];
  sort_by: string;
  sort_order: 'asc' | 'desc';
  expandReleaseDate?: boolean;
};

interface GamesSectionProps {
  icon: ReactNode;
  titleId: string;
  titleDefaultMessage: string;
  queryParams: Record<string, string>;
  showLoading?: boolean;
  rootStyles?: React.CSSProperties;
  onMouseOver?: () => void;
  onMouseLeave?: () => void;
  viewAllTo?: ViewAllTo;
  showExportAction?: boolean;
  /** Section name reported to GA, e.g. `latest_releases`. */
  gaSection?: string;
}

const buildGamesSearchParams = (viewAllTo: ViewAllTo): string => {
  const params = new URLSearchParams();
  viewAllTo.game_status.forEach((s) =>
    params.append('game_status', s),
  );
  params.set('sort_by', viewAllTo.sort_by);
  params.set('sort_order', viewAllTo.sort_order);
  if (viewAllTo.expandReleaseDate) {
    params.set('expand_release_date', '1');
  }
  return params.toString();
};

const GamesSection = ({
  icon,
  titleId,
  titleDefaultMessage,
  queryParams,
  showLoading = true,
  rootStyles,
  onMouseOver,
  onMouseLeave,
  viewAllTo,
  showExportAction = false,
  gaSection,
}: GamesSectionProps) => {
  const { data: gamesData, isLoading } = useQuery({
    queryKey: ['games', ...Object.values(queryParams), 4],
    queryFn: () =>
      fetchGamesGroupedByGameGroup({ ...queryParams, limit: 4 }),
  });
  const gameGroups = gamesData?.games_by_group
    ? gamesData.games_by_group
        .map((group) => group.games)
        .filter((groupGames) => groupGames && groupGames.length > 0)
    : undefined;
  const displayedGroups = gameGroups?.slice(0, 4);

  const handleViewAllClick = () => {
    ga.trackMouseClick({
      component: 'view_all',
      section: gaSection || titleDefaultMessage,
      destination_page: ga.PAGES.games,
      ...(viewAllTo?.game_status?.length
        ? { game_status: viewAllTo.game_status.join(',') }
        : {}),
    });
  };

  return (
    <Root
      style={rootStyles}
      onMouseOver={onMouseOver}
      onMouseLeave={onMouseLeave}
    >
      <Top>
        <TopLeft>
          {icon}
          <FormattedMessage
            id={titleId}
            defaultMessage={titleDefaultMessage}
          />
        </TopLeft>
        <TopRight>
          {showExportAction && (
            <>
              <ExportToExcelButton />
              <Divider />
            </>
          )}
          {viewAllTo ? (
            <Button
              variant="text"
              component={Link}
              to={`/games?${buildGamesSearchParams(viewAllTo)}`}
              onClick={handleViewAllClick}
            >
              View All
            </Button>
          ) : (
            <Button variant="text" onClick={handleViewAllClick}>
              View All
            </Button>
          )}
        </TopRight>
      </Top>
      <List>
        {isLoading && showLoading && (
          <LoaderWrapper role="status" aria-label="Loading items">
            <CircularProgress />
          </LoaderWrapper>
        )}
        {displayedGroups?.map((groupGames, index) => {
          const gameStatusScope = viewAllTo?.game_status;
          const defaultGame =
            getFirstGameByRTP(
              groupGames,
              gameStatusScope,
              queryParams.sort_by,
              queryParams.sort_order as 'asc' | 'desc' | undefined,
            ) || groupGames[0];
          return (
            <GameCard
              key={defaultGame.id || index}
              games={groupGames}
              showDownload={false}
              defaultGame={defaultGame}
              section={titleDefaultMessage}
              showReleaseDateIcon
            />
          );
        })}
      </List>
    </Root>
  );
};

const Root = styled('div')(() => ({}));

const Top = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: 12,
  fontSize: 18,
  lineHeight: '28px',
  fontWeight: 600,
  color: theme.palette.textPrimaryBlack,
  padding: 16,
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const TopLeft = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: 12,
  alignItems: 'center',

  svg: {
    width: 24,
    height: 24,

    path: {
      stroke: theme.palette.primary.main,
    },
  },
}));

const TopRight = styled('div')(() => ({
  display: 'flex',
  gap: 4,
  alignItems: 'center',
}));

const Divider = styled('div')(({ theme }) => ({
  width: 1,
  height: 20,
  backgroundColor: theme.palette.grey300,
}));

const List = styled('div')(() => ({
  display: 'flex',
  gap: '26px',
  flexDirection: 'row',
  flexWrap: 'wrap',
  minHeight: '420px',

  '> div': {
    flexShrink: 0,
    alignSelf: 'center',
  },
}));

const LoaderWrapper = styled('div')({
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

export default GamesSection;
